import { apiRequest } from '@/shared/lib/api-client';
import { SYNC_MARK_KEY, SYNC_MARK_LOCAL_KEY } from '@/shared/lib/query-cache';
import { queryClient as qc } from '@/shared/lib/query-client';
import { useAuthStore } from '@/shared/stores/auth-store';
import type { Query } from '@tanstack/react-query';
import { applyTicketCard, debugInvalidate, listaComBuscaOuTriagem } from './inbox-debug';
import { useOpenTicketStore } from './stores/open-ticket-store';
import type { TicketSummary } from './types';

/**
 * Serviço de reconexão — "o que eu perdi enquanto estive fora?".
 *
 * ## O problema
 *
 * Enquanto o socket está fora do ar os avisos se perdem: ninguém os guarda pra
 * entregar depois. A pergunta é o que fazer ao voltar.
 *
 * A resposta anterior era desconfiar de TUDO: marcar todas as conversas como
 * velhas e conferir cada uma que o atendente abrisse. Três defeitos vieram daí:
 *
 *   1. Pagava-se releitura até do que não mudou (abrir a mesma conversa pela
 *      terceira vez custava três buscas).
 *   2. A conferência chegava atrasada e bagunçava a tela (a conversa abria,
 *      rolava, e meio segundo depois vinham mensagens que faltavam).
 *   3. Era frágil: vivia num `useEffect` da tela de Atendimento. Quem estava no
 *      Dashboard quando a linha voltou não desconfiava de nada — e o painel
 *      mostrava dado velho com toda a confiança até um F5.
 *
 * Aqui a pergunta é uma só e a resposta traz apenas o que mudou, já em formato
 * de cartão. O encaixe usa o MESMO caminho dos avisos ao vivo, então as
 * correções aparecem como mensagens chegando — sem tela de carregamento.
 *
 * ## Por que nasce junto com o socket, e não num componente
 *
 * `SocketProxy.on()` não repete evento passado. Se `connect` disparasse antes
 * de o React registrar o ouvinte, a recuperação inteira se perdia — e é
 * justamente na volta rápida (notebook reaberto, aba retomada) que ele dispara
 * cedo. Instalar aqui, na mesma volta síncrona em que o socket é construído,
 * torna essa corrida impossível: a mensagem do Worker só chega no próximo
 * ciclo. De quebra, funciona em qualquer rota — Dashboard, Canais, Configurações.
 */

interface RespostaMudancas {
  recarregue: boolean;
  tickets?: TicketSummary[];
  agora: string;
}

function lerMarca(): string | null {
  try {
    return localStorage.getItem(SYNC_MARK_KEY);
  } catch {
    return null; // modo privativo / armazenamento cheio → cai no "recarregue"
  }
}

function gravarMarca(agora: string): void {
  try {
    localStorage.setItem(SYNC_MARK_KEY, agora);
    // Hora local do mesmo instante, junto com a própria marca: o preparo da
    // entrada compara com o carimbo da cópia salva no MESMO relógio, e só confia
    // no par se a marca guardada for a atual (vide SYNC_MARK_LOCAL_KEY).
    localStorage.setItem(SYNC_MARK_LOCAL_KEY, `${Date.now()}|${agora}`);
  } catch {
    // Sem marca, a próxima reconexão recarrega. Degrada, não quebra.
  }
}

/**
 * A marca DESTA aba, depois da primeira sincronia.
 *
 * O localStorage é um só para todas as abas do navegador. Antes, cada
 * reconexão lia a marca dali — e a aba que voltava por último lia a marca que a
 * OUTRA acabou de gravar: duas abas, queda das 10:00 às 10:05, mensagem nesse
 * meio; a aba 2 volta, pergunta "desde 10:00" e grava 10:05; a aba 1 volta
 * logo depois, pergunta "desde 10:05", recebe nada e fica com a conversa sem a
 * mensagem. Agora só a ENTRADA lê o localStorage (é a passagem de bastão do F5,
 * já ajustada pelo preparo da cópia); dali em diante cada aba pergunta desde a
 * própria marca. Mesma consulta de sempre, só com o `desde` certo.
 */
let marcaDestaAba: string | null = null;

/**
 * Quando esta carga de página começou, e se a sincronia da entrada ainda não
 * rodou — vide `desconfiarDeTudo`. Vale só a PRIMEIRA tentativa, deu certo ou
 * não.
 */
let inicioDaCarga = Date.now();
let naEntrada = true;

function concluirSincronia(agora: string): void {
  gravarMarca(agora);
  marcaDestaAba = agora;
}

/** Volta ao estado de página recém-carregada (testes). */
export function reiniciarSincroniaDaCarga(): void {
  marcaDestaAba = null;
  inicioDaCarga = Date.now();
  naEntrada = true;
  descartarAoSairDaTela.clear();
}

/**
 * Cópia que deixou de ser confiável: FORA da tela é DESCARTADA, NA tela é
 * rebuscada.
 *
 * Antes as duas eram só marcadas como velhas (`isInvalidated`). Não basta: no
 * TanStack, qualquer `setQueryData` apaga essa marca e renova o
 * `dataUpdatedAt` — mesmo sem mudar nada —, e todo aviso ao vivo entra por
 * `setQueryData` (o encaixe). A cópia velha passava por nova. Reproduzido no
 * srv03 em 03/nov nos três casos reais:
 *
 *   - Jéssica: a conversa marcada recebeu o "ta bem" e abriu sem buscar,
 *     faltando o meio — só outro F5 corrigia;
 *   - Rafael (São Bento): a lista "Visualizar todos" estava desligada quando o
 *     PC acordou; avisos de colegas a desmarcaram e, ligada, ela mostrou em
 *     Ativos um atendimento fechado havia duas horas;
 *   - "todos" com cartões antigos logo depois do F5, pelo mesmo caminho.
 *
 * Sem cópia não há onde encaixar (todo encaixe começa com `if (!prev)`), e a
 * próxima abertura vem inteira do servidor: a mesma busca que a marca já
 * deveria provocar, sem a chance de a cópia velha passar por nova. Na tela não
 * dá pra descartar (piscaria em branco debaixo de quem lê); ali a rebusca é
 * imediata — e, se ela falhar, a cópia vai embora quando sair da tela.
 */
function descartarOuRebuscar(queries: Query[], rebuscar: (naTela: Query[]) => Promise<void>): void {
  const naTela: Query[] = [];
  for (const q of queries) {
    if (q.getObserversCount() > 0) naTela.push(q);
    else qc.getQueryCache().remove(q);
  }
  if (naTela.length === 0) return;
  const errosAntes = new Map(naTela.map((q) => [q, q.state.errorUpdateCount]));
  // Primeira busca ainda em voo (sem dado, ex. "Visualizar todos" recém-ligada):
  // o TanStack não a cancela num invalidate — junta a rebusca nela, e vale a
  // resposta de ANTES da volta (medido no srv03). Cancela primeiro.
  const emVooSemDado = naTela.filter(
    (q) => q.state.data === undefined && q.state.fetchStatus === 'fetching',
  );
  void Promise.all(emVooSemDado.map((q) => qc.cancelQueries({ queryKey: q.queryKey, exact: true })))
    .then(() => rebuscar(naTela))
    .then(() => {
      for (const [q, antes] of errosAntes) {
        if (q.state.errorUpdateCount > antes) descartarAoSairDaTela.add(q.queryHash);
      }
    });
}

/** Conversas e cabeçalhos: cada cópia com a sua rebusca. */
function descartarOuRebuscarChave(chave: readonly unknown[], exata = false): void {
  descartarOuRebuscar(
    qc.getQueryCache().findAll({ queryKey: [...chave], exact: exata }),
    (naTela) =>
      Promise.all(
        naTela.map((q) => qc.invalidateQueries({ queryKey: q.queryKey, exact: true })),
      ).then(() => undefined),
  );
}

/** Listas: a rebusca passa pelo `debugInvalidate` (etiqueta e contadores). */
function descartarOuRebuscarListas(motivo: string, quais: (q: Query) => boolean): void {
  descartarOuRebuscar(
    qc.getQueryCache().findAll({ queryKey: ['tickets'], predicate: quais }),
    (naTela) => debugInvalidate(qc, ['tickets'], 'reconexao', motivo, (q) => naTela.includes(q)),
  );
}

/**
 * Cópias que estavam NA tela, mas cuja rebusca falhou (rede caindo justo na
 * volta). Seguem velhas e o próximo aviso as desmarcaria — então, quando saem
 * da tela, são descartadas. Uma busca bem-sucedida as tira da lista.
 */
const descartarAoSairDaTela = new Set<string>();

const cachesVigiados = new WeakSet<object>();

function vigiarCopiasSuspeitas(): void {
  const cache = qc.getQueryCache();
  if (cachesVigiados.has(cache)) return;
  cachesVigiados.add(cache);
  cache.subscribe((evento) => {
    if (descartarAoSairDaTela.size === 0) return;
    const hash = evento.query.queryHash;
    if (!descartarAoSairDaTela.has(hash)) return;
    if (evento.type === 'updated' && evento.action.type === 'success' && !evento.action.manual) {
      descartarAoSairDaTela.delete(hash);
      return;
    }
    if (evento.type === 'observerRemoved' && evento.query.getObserversCount() === 0) {
      descartarAoSairDaTela.delete(hash);
      // Fora da volta da notificação: o TanStack ainda está tirando o observador.
      queueMicrotask(() => {
        if (evento.query.getObserversCount() === 0) qc.getQueryCache().remove(evento.query);
      });
    }
  });
}

/**
 * O caminho antigo: tudo vira suspeito. Rede de segurança do delta.
 *
 * Na ENTRADA (F5, login, aba aberta), as listas a tela de Atendimento busca
 * sozinha ao montar. E na entrada o servidor quase sempre manda recarregar (a
 * última marca tem mais de 30 min: primeira abertura do dia, volta do almoço)
 * — aí a mesma tela buscava as 3 listas DE NOVO, Fechados com todas as páginas
 * guardadas (abertura em dobro, medida no srv03 em 03/nov). Na entrada só é
 * suspeita a lista que ainda não foi buscada nesta carga de página e não está
 * sendo buscada agora. Nas reconexões do meio do expediente, todas.
 */
function desconfiarDeTudo(motivo: string, entrada: boolean): void {
  descartarOuRebuscarChave(['ticket-messages']);
  descartarOuRebuscarChave(['ticket']);
  descartarOuRebuscarListas(motivo, (q) =>
    entrada ? q.state.fetchStatus !== 'fetching' && q.state.dataUpdatedAt < inicioDaCarga : true,
  );
}

async function sincronizar(): Promise<void> {
  const entrada = naEntrada;
  naEntrada = false;
  const marca = marcaDestaAba ?? lerMarca();
  let r: RespostaMudancas;
  try {
    const qs = marca ? `?desde=${encodeURIComponent(marca)}` : '';
    r = await apiRequest<RespostaMudancas>(`/tickets/changes${qs}`);
  } catch {
    // Servidor fora, rede instável, sessão expirando: sem resposta não há como
    // saber o que mudou. Desconfia de tudo — exatamente o comportamento antigo.
    desconfiarDeTudo('delta_falhou', entrada);
    return;
  }

  if (r.recarregue) {
    desconfiarDeTudo(marca ? 'janela_grande' : 'sem_marca', entrada);
    concluirSincronia(r.agora);
    return;
  }

  const cartoes = r.tickets ?? [];
  const eu = useAuthStore.getState().user;

  if (cartoes.length > 0) {
    if (!eu) {
      // Sem usuário em memória não dá pra decidir em qual lista cada cartão
      // entra (`belongsTo` depende de perfil e departamentos).
      desconfiarDeTudo('sem_usuario', entrada);
      concluirSincronia(r.agora);
      return;
    }
    const viewer = {
      id: eu.id,
      profile: eu.profile,
      departmentIds: eu.departmentIds,
      support: Boolean(eu.impersonating),
    };
    for (const card of cartoes) {
      applyTicketCard(qc, card, viewer, { origin: 'reconexao', event: 'delta' });
      /**
       * Conversa que mudou: fora da tela, a cópia é DESCARTADA — abrir mostra
       * "carregando" e traz a conversa inteira; quem não mudou continua abrindo
       * da cópia na hora. Vale na entrada E nas reconexões do meio do
       * expediente: antes estas só marcavam, e o primeiro aviso daquela
       * conversa apagava a marca (vide `descartarOuRebuscar`).
       */
      descartarOuRebuscarChave(['ticket-messages', card.id], true);
      descartarOuRebuscarChave(['ticket', card.id], true);
    }
    // Listas com busca ou triagem ligada não são encaixáveis — só o servidor
    // sabe o que casa com o filtro.
    descartarOuRebuscarListas('delta', (q) => listaComBuscaOuTriagem(q.queryKey));
  }

  /**
   * A conversa ABERTA confere sempre, mesmo fora da lista de mudanças.
   *
   * O delta enxerga o que mexe no TICKET. Confirmação de entrega e de leitura
   * mexem só na MENSAGEM — o relógio da bolha virar ✓✓ não bumpa nada no
   * ticket. Era o caso relatado: "eu longe da tela, a atendente respondeu, e
   * quando abri as bolhas estavam paradas no relógio".
   *
   * Custa no máximo uma busca (só existe uma conversa aberta), e só quando o
   * socket volta. É seguro chamar mesmo se o cartão já invalidou acima: o
   * TanStack junta buscas concorrentes da mesma chave.
   */
  const aberta = useOpenTicketStore.getState().openTicketId;
  if (aberta !== null) descartarOuRebuscarChave(['ticket-messages', aberta], true);

  concluirSincronia(r.agora);
}

/**
 * O mínimo do socket que este serviço usa. Tipado por forma, não por classe,
 * pra que o teste possa passar um dublê sem construir um Web Worker.
 */
interface SocketMinimo {
  // biome-ignore lint/suspicious/noExplicitAny: espelha o Listener do SocketProxy
  on: (evento: string, fn: (...args: any[]) => void) => unknown;
  // biome-ignore lint/suspicious/noExplicitAny: idem
  emit: (evento: string, ...args: any[]) => unknown;
}

/**
 * Liga o serviço a um socket. Chamada UMA vez, dentro do `getSocket()`, na
 * mesma volta síncrona da construção — vide o comentário do topo.
 */
export function instalarReconexao(socket: SocketMinimo): void {
  vigiarCopiasSuspeitas();
  socket.on('connect', () => {
    /**
     * Re-entrar nas salas ANTES de perguntar o que mudou.
     *
     * Ordem importa: se algo acontecer entre a resposta e o re-join, o aviso
     * não chega e a mudança fica fora dos dois caminhos. Entrando primeiro, o
     * pior caso é receber o mesmo fato duas vezes — e encaixar o mesmo cartão
     * duas vezes não faz diferença.
     *
     * As três salas da inbox o servidor já garante sozinho no `connection`;
     * mantidas aqui porque são idempotentes e o piso não custa nada. A da
     * conversa é só do cliente — o servidor não sabe qual está aberta.
     */
    socket.emit('joinInbox', 'active');
    socket.emit('joinInbox', 'pending');
    socket.emit('joinInbox', 'closed');
    const aberta = useOpenTicketStore.getState().openTicketId;
    if (aberta !== null) socket.emit('joinTicket', aberta);

    void sincronizar();
  });
}
